import React from 'react';
import {
  Clock,
  CheckCircle,
  TrendingUp,
  TrendingDown,
  FileCheck,
  List,
  Ban,
  Truck,
  PackageCheck,
  DollarSign,
  Check,
  Gavel,
  Building2,
  Users,
  Maximize2,
  ChevronRight,
} from 'lucide-react';
import '../../../../../styles/components/delivery-status-card.scss';
import { OMR } from '../../../../common/icons';
import i18n from '../../../../../i18n';

// icon + colour per delivery status – the colour drives the whole card (icon, border, bar)
export const STATUS_META = {
  pending: { icon: Clock, tone: '#f59e0b' },
  approved: { icon: CheckCircle, tone: '#16a34a' },
  rejected: { icon: Ban, tone: '#dc2626' },
  fundAdded: { icon: TrendingUp, tone: '#2563eb' },
  fundDeduct: { icon: TrendingDown, tone: '#dc2626' },
  financeSettled: { icon: FileCheck, tone: '#16a34a' },
  deliveryStarted: { icon: Truck, tone: '#0ea5e9' },
  deliveryCompleted: { icon: PackageCheck, tone: '#16a34a' },
  all: { icon: List, tone: '#1b8f9b' },
  default: { icon: DollarSign, tone: '#1b8f9b' },
};

const fmt = (n, decimals = 2) =>
  new Intl.NumberFormat('en', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(n) || 0);

const fmtCount = (n) => new Intl.NumberFormat('en').format(Number(n) || 0);

/**
 * Status card for the delivery / auction-approval list.
 *
 * props
 *  type, title, amount, auctionCount, clientsCount, biddersCount
 *  paidAmount, pendingAmount   – bidder payment amounts
 *  showClients, showPayments   – hide rows per role
 *  selected, onSelect, loading
 *  onDetails(type, tab)        – opens the detailed listing (optional)
 */
export const DeliveryStatusCard = ({
  type,
  title,
  amount = 0,
  auctionCount = 0,
  clientsCount = 0,
  biddersCount = 0,
  paidAmount = 0,
  pendingAmount = 0,
  showClients = true,
  showPayments = true,
  selected = false,
  onSelect,
  onDetails,
  loading = false,
  decimals = 2,
}) => {
  const { icon: Icon, tone } = STATUS_META[type] || STATUS_META.default;

  const paid = Number(paidAmount) || 0;
  const pending = Number(pendingAmount) || 0;
  const payTotal = paid + pending;
  const paidPct = payTotal > 0 ? (paid / payTotal) * 100 : 0;

  const select = () => onSelect?.(type);
  const onKeyDown = (e) => {
    if (e.target !== e.currentTarget) return; // keys on inner buttons are theirs
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      select();
    }
  };

  // inner buttons open the modal without also selecting the card
  const openDetails = (tab) => (e) => {
    e.stopPropagation();
    onDetails?.(type, tab);
  };

  // a count chip is a button only when the modal is available
  const Count = ({ icon: CIcon, value, label, tab }) =>
    onDetails ? (
      <button
        type="button"
        className="ds-count is-link"
        onClick={openDetails(tab)}
        title={i18n.t('ds_view_list', { defaultValue: 'View {{label}}', label })}
      >
        <CIcon size={13} />
        <b>{fmtCount(value)}</b>
        {label}
      </button>
    ) : (
      <span className="ds-count" title={label}>
        <CIcon size={13} />
        <b>{fmtCount(value)}</b>
        {label}
      </span>
    );

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      className={`ds-card ${selected ? 'is-selected' : ''}`}
      style={{ '--ds-tone': tone }}
      onClick={select}
      onKeyDown={onKeyDown}
    >
      {/* head */}
      <div className="ds-head">
        <span className="ds-icon">
          <Icon size={18} strokeWidth={2.2} />
        </span>
        <span className="ds-title">{title}</span>
        {selected && (
          <span className="ds-check" aria-hidden="true">
            <Check size={12} strokeWidth={3} />
          </span>
        )}
        {onDetails && (
          <button
            type="button"
            className="ds-more"
            onClick={openDetails('auctions')}
            aria-label={i18n.t('ds_view_details', { defaultValue: 'View details' })}
            title={i18n.t('ds_view_details', { defaultValue: 'View details' })}
          >
            <Maximize2 size={14} />
          </button>
        )}
      </div>

      {/* amount */}
      {loading ? (
        <div className="ds-skel ds-skel--amount" />
      ) : (
        <div className="ds-amount">
          <OMR size={13} />
          <span>{fmt(amount, decimals)}</span>
        </div>
      )}

      {/* counts */}
      {loading ? (
        <div className="ds-skel ds-skel--counts" />
      ) : (
        <div className="ds-counts">
          <Count icon={Gavel} value={auctionCount} label={i18n.t('auctions')} tab="auctions" />
          {showClients && (
            <Count
              icon={Building2}
              value={clientsCount}
              label={i18n.t('ds_clients', { defaultValue: 'Clients' })}
              tab="clients"
            />
          )}
          <Count
            icon={Users}
            value={biddersCount}
            label={i18n.t('ds_bidders', { defaultValue: 'Bidders' })}
            tab="bidders"
          />
        </div>
      )}

      {/* bidder payments */}
      {showPayments && (
        <div className="ds-pay">
          <div className="ds-pay-head">
            <span>{i18n.t('ds_bidder_payments', { defaultValue: 'Bidder payments' })}</span>
            {!loading && payTotal > 0 && (
              <span className="ds-pay-pct">
                {paidPct.toFixed(0)}% {i18n.t('ds_paid', { defaultValue: 'paid' })}
              </span>
            )}
          </div>

          {loading ? (
            <div className="ds-skel ds-skel--pay" />
          ) : (
            <>
              <div
                className={`ds-bar ${payTotal > 0 ? '' : 'is-empty'}`}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(paidPct)}
              >
                <span style={{ width: `${paidPct}%` }} />
              </div>

              <div className="ds-pay-row is-paid">
                <i className="ds-dot" />
                <span className="ds-pay-label">
                  {i18n.t('ds_payment_completed', { defaultValue: 'Completed' })}
                </span>
                <span className="ds-pay-val">
                  <OMR size={10} />
                  {fmt(paid, decimals)}
                </span>
              </div>

              <div className="ds-pay-row is-pending">
                <i className="ds-dot" />
                <span className="ds-pay-label">
                  {i18n.t('ds_payment_pending', { defaultValue: 'Pending' })}
                </span>
                <span className="ds-pay-val">
                  <OMR size={10} />
                  {fmt(pending, decimals)}
                </span>
              </div>

              {onDetails && (
                <button type="button" className="ds-pay-link" onClick={openDetails('payments')}>
                  {i18n.t('ds_view_payments', { defaultValue: 'View payments' })}
                  <ChevronRight size={14} />
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DeliveryStatusCard;
